<script setup>
import { computed, ref } from 'vue'
import { CircleHelp, MoreHorizontal, Settings } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { mainNavigation } from '@/config/navigation'

const route = useRoute()
const moreMenu = ref(null)
const mobileNavigation = computed(() => mainNavigation.filter((item) => !item.desktopOnly))
const moreMenuActive = computed(() => ['settings', 'getting-started'].includes(route.name))

function isMobileNavigationActive(item) {
  return route.name === item.name
    || route.matched.some((record) => record.meta.sidebarGroup === item.name)
}

function closeMoreMenu() {
  moreMenu.value?.removeAttribute('open')
}
</script>

<template>
  <nav class="mobile-bottom-nav">
    <RouterLink
      v-for="item in mobileNavigation"
      :key="item.name"
      :to="{ name: item.name }"
      class="mobile-nav-item"
      :class="{ 'mobile-nav-item--active': isMobileNavigationActive(item) }"
    >
      <component
        :is="item.icon"
        :size="21"
        :stroke-width="1.8"
      />
      <span>{{ $t(`navigation.${item.name}`) }}</span>
    </RouterLink>

    <details
      ref="moreMenu"
      class="more-menu"
    >
      <summary
        class="mobile-nav-item more-menu-trigger"
        :class="{ 'mobile-nav-item--active': moreMenuActive }"
      >
        <MoreHorizontal
          :size="21"
          :stroke-width="1.8"
        />
        <span>{{ $t('navigation.more') }}</span>
      </summary>

      <div class="more-menu-dropdown">
        <RouterLink
          :to="{ name: 'settings' }"
          class="more-menu-item"
          @click="closeMoreMenu"
        >
          <Settings :size="18" :stroke-width="1.8" />
          <span>{{ $t('navigation.settings') }}</span>
        </RouterLink>
        <RouterLink
          :to="{ name: 'getting-started' }"
          class="more-menu-item"
          @click="closeMoreMenu"
        >
          <CircleHelp :size="18" :stroke-width="1.8" />
          <span>{{ $t('navigation.gettingStarted') }}</span>
        </RouterLink>
      </div>
    </details>
  </nav>
</template>

<style scoped>
.mobile-bottom-nav {
  display: none;
}

@media (max-width: 1023px) {
  .mobile-bottom-nav {
    position: fixed;
    z-index: 50;
    right: 0;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    height: 72px;
    padding: 7px 8px calc(7px + env(safe-area-inset-bottom));
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    align-items: center;
    background: var(--surface-card);
    border-top: 1px solid #e8e8ed;
  }
  .mobile-nav-item {
    height: 52px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 0;
    color: #555563;
    text-decoration: none;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 600;
  }
  .mobile-nav-item.router-link-active,
  .mobile-nav-item.mobile-nav-item--active {
    color: #ffffff;
    background: #5b55df;
  }
  .more-menu {
    position: relative;
    min-width: 0;
  }
  .more-menu summary {
    list-style: none;
    cursor: pointer;
  }
  .more-menu summary::-webkit-details-marker {
    display: none;
  }
  .more-menu-trigger.mobile-nav-item--active {
    color: #ffffff;
    background: #5b55df;
  }
  .more-menu-dropdown {
    position: absolute;
    right: 0;
    bottom: calc(100% + 8px);
    z-index: 1;
    min-width: 176px;
    padding: 5px;
    background: var(--surface-card);
    border: 1px solid #dedee8;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgb(44 43 93 / 14%);
  }
  .more-menu-item {
    padding: 10px 11px;
    display: flex;
    align-items: center;
    gap: 9px;
    color: #303142;
    text-decoration: none;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
  }
  .more-menu-item:hover,
  .more-menu-item.router-link-active {
    color: #3832cf;
    background: #efefff;
  }
}
</style>
